import { Tabs } from "antd";
import { lazy, useEffect, useMemo, useState } from "react";
import { type IntlShape, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import usePermissionsByCompetitionName from "../hooks/usePermissionsByCompetitionName";

const ManageOrganizationsWrapper = lazy(() => import("../Features/Organizations/ManageOrganizationsWrapper.tsx"));
const Clubs = lazy(() => import("../Features/Organizations/Tabs/Clubs.jsx"));
const Competitions = lazy(() => import("../Features/Organizations/Tabs/Competitions.jsx"));
const Conferences = lazy(() => import("../Features/Organizations/Tabs/Conferences.jsx"));
const Dashboard = lazy(() => import("../Features/Organizations/Tabs/Dashboard.jsx"));
const LeadersQualification = lazy(() => import("../Features/Organizations/Tabs/LeadersQualification.jsx"));
const Leagues = lazy(() => import("../Features/Organizations/Tabs/Leagues.jsx"));
const MatchProfiles = lazy(() => import("../Features/Organizations/Tabs/MatchProfiles.jsx"));
const Persons = lazy(() => import("../Features/Organizations/Tabs/Persons.jsx"));
const Sites = lazy(() => import("../Features/Organizations/Tabs/Sites.jsx"));
const StandingsConfiguration = lazy(() => import("../Features/Organizations/Tabs/StandingsConfiguration.jsx"));
const Teams = lazy(() => import("../Features/Organizations/Tabs/Teams.jsx"));
const Venues = lazy(() => import("../Features/Organizations/Tabs/Venues.jsx"));
const Conducts = lazy(() => import("~pages/Conducts/ConductsPage.tsx"));
const PermissionUserListPage = lazy(() => import("~pages/Permissions/PermissionUserListPage/PermissionUserListPage.tsx"));

const getItems = (entityPermissionName: PermissionNames | undefined, formatMessage: IntlShape["formatMessage"]) => [
  {
    key: "admin",
    label: formatMessage({ id: "dashboard" }),
    children: <Dashboard />,
  },
  {
    key: "leagues",
    label: formatMessage({ id: "leagues" }),
    children: <Leagues />,
    permissionKey: PermissionNames.LEAGUE,
  },
  {
    key: "competitions",
    label: formatMessage({ id: "competitions" }),
    children: <Competitions />,
    permissionKey: PermissionNames.COMPETITION,
  },
  {
    key: "conferences",
    label: formatMessage({ id: "conferences" }),
    children: <Conferences />,
    permissionKey: PermissionNames.CONFERENCE,
  },
  {
    key: "clubs",
    label: formatMessage({ id: "clubs" }),
    children: <Clubs />,
    permissionKey: PermissionNames.ENTITY_GROUP,
  },
  {
    key: "teams",
    label: formatMessage({ id: "teams" }),
    children: <Teams />,
    permissionKey: entityPermissionName,
  },
  {
    key: "sites",
    label: formatMessage({ id: "sites" }),
    children: <Sites />,
    permissionKey: PermissionNames.SITE,
  },
  {
    key: "venues",
    label: formatMessage({ id: "venues" }),
    children: <Venues />,
    permissionKey: PermissionNames.VENUE,
  },
  {
    key: "persons",
    label: formatMessage({ id: "people" }),
    children: <Persons />,
    permissionKey: PermissionNames.PERSON,
  },
  {
    key: "permissions",
    label: formatMessage({ id: "permissions" }),
    children: <PermissionUserListPage />,
    permissionKey: PermissionNames.PERMISSIONS,
  },
  {
    key: "standings-configurations",
    label: formatMessage({ id: "standings.configurations" }),
    children: <StandingsConfiguration />,
    permissionKey: PermissionNames.STANDING_CONFIGURATION,
  },
  {
    key: "leaders-qualification",
    label: formatMessage({ id: "leaders.qualification" }),
    children: <LeadersQualification />,
    permissionKey: PermissionNames.LEADER_QUALIFIER,
  },
  {
    key: "match-profiles",
    label: formatMessage({ id: "match.profiles" }),
    children: <MatchProfiles />,
    permissionKey: PermissionNames.MATCH_PROFILE,
  },
  {
    key: "conducts",
    label: formatMessage({ id: "conduct" }),
    children: <Conducts />,
    permissionKey: PermissionNames.CONDUCT,
  },
];

const OrganizationRoutes = () => {
  const navigate = useNavigate();
  const [tabFromURL, setTabFromURL] = useState<string>();
  const { tab, customerId, sport, organizationId } = useParams();
  const { permissions } = useUserPermissions();
  const { entityPermissionName } = usePermissionsByCompetitionName();
  const { formatMessage } = useIntl();

  const items = useMemo(() => {
    return getItems(entityPermissionName, formatMessage);
  }, [entityPermissionName, formatMessage]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => !item.permissionKey || permissions[item.permissionKey]?.includes(PermissionOperations.READ));
  }, [permissions, items]);

  useEffect(() => {
    if (filteredItems.length > 0 && !filteredItems.some((item) => item.key === tab)) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/403`, {
        state: {
          back: `/${customerId}/admin/${sport}/${organizationId}/admin`,
        },
      });
    }
  }, [filteredItems, customerId, sport, tab, organizationId, navigate]);

  useEffect(() => {
    setTabFromURL(tab);
  }, [tab]);

  return (
    <ManageOrganizationsWrapper>
      <div className="card-container tabs-wrap">
        <Tabs
          destroyInactiveTabPane={true}
          tabBarGutter={0}
          activeKey={tabFromURL}
          defaultActiveKey={"admin"}
          onChange={(key) => {
            navigate(`/${customerId}/admin/${sport}/${organizationId}/${key}`);
          }}
          items={filteredItems}
        />
      </div>
    </ManageOrganizationsWrapper>
  );
};

export default OrganizationRoutes;
